import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import type {
  Commune,
  Antenne,
  SimpleCommuneAvecStatutAffectation,
  AffectationAntenneDto,
} from "models";
import { antennesServices, communesService, networkService } from "services";
import { Autocomplete, Button, Chip, Grid, TextField, Typography } from "@mui/material";
import { SdeappsError, useSnackbarErrorHandler } from "utils/errorHandling";
import { routesConfig } from "config/app-config";
import { AffectationAntenneCommunesLegende } from "./AffectationAntenneCommunesLegende";
import {
  filterOptionsAntenne,
  isOptionEqualToValue,
  getOptionLabel,
  getOptionKey,
  filterOptionsCommune,
} from "../utils/affectationOptionsUtils";
import { getColorFrom } from "../utils/chipColorUtils";
import useSWR from "swr";
import { createSwrConfiguration } from "utils";
import { enqueueSnackbar } from "notistack";
import ToastMessages from "constants/ToastMessages";

async function isAffectationReplicated(expectedAntenne: Antenne): Promise<boolean> {
  const antenne = await antennesServices.getById(expectedAntenne.id);
  const actualIds = antenne.idCommunes ?? [];
  const expectedIds = expectedAntenne.idCommunes ?? [];
  // La réplication est effective si:
  return (
    // le détail actuel contient tous les codes INSEE attendus
    actualIds.every((id) => expectedIds.includes(id)) &&
    // et que l'objet attendu a bien les codes INSEE présents dans l'actuel détail.
    expectedIds.every((id) => actualIds.includes(id))
  );
}

export function AffectationAntenneCommunesForm(): ReactNode {
  const { isLoading: isSaving, catchErrors: catchErrorsOnSave } = useSnackbarErrorHandler({
    defaultIsLoading: false,
  });

  const [selectedAntenne, setSelectedAntenne] = useState<Antenne>();
  const [affectations, setAffectations] = useState<Array<SimpleCommuneAvecStatutAffectation>>([]);

  const {
    data: antennes = [],
    isLoading: isLoadingAntennes,
    mutate: mutateAntennes,
  } = useSWR(
    "/antennes/getAll",
    antennesServices.getAll,
    createSwrConfiguration({ dedupingInterval: 0 })
  );
  const { data: communes = [], isLoading: isLoadingCommunes } = useSWR(
    "/communes/getAll",
    communesService.getAll,
    createSwrConfiguration()
  );

  const onAntenneChange = useCallback(
    (antenne: Antenne | null) => {
      setSelectedAntenne(antenne ?? undefined);
      const selectionFromAntenne =
        antenne?.idCommunes == null || antenne?.idCommunes.length === 0
          ? []
          : communes.filter(({ id }) => antenne.idCommunes?.includes(id) ?? false);
      setAffectations(
        selectionFromAntenne.map(({ id, libelle }) => ({
          id,
          libelle,
          statut: "affectée",
        }))
      );
    },
    [communes]
  );

  const handleAntenneOnChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, antenne: Antenne | null) => {
      onAntenneChange(antenne);
    },
    [onAntenneChange]
  );

  const handleCommuneOnSelect = useCallback(
    (_: React.SyntheticEvent<Element, Event>, commune: Commune | null) => {
      if (commune == null) {
        return;
      }

      let nouvellesAffectations = [...affectations];
      if (selectedAntenne?.idCommunes?.includes(commune.id) ?? false) {
        nouvellesAffectations = nouvellesAffectations.map((affectation) => {
          if (affectation.id === commune.id) {
            return {
              ...affectation,
              statut: "affectée",
            };
          }
          return affectation;
        });
      } else {
        const { id, libelle } = commune;
        const nouvelleAffectation: SimpleCommuneAvecStatutAffectation = {
          id,
          libelle,
          statut: "nouvelle-affectation",
        };
        nouvellesAffectations.push(nouvelleAffectation);
      }

      setAffectations(nouvellesAffectations.toSorted((a, b) => a.libelle.localeCompare(b.libelle)));
    },
    [affectations, selectedAntenne?.idCommunes]
  );

  const handleOnDelete = useCallback(
    (commune: SimpleCommuneAvecStatutAffectation) => () => {
      const updatedAffectations: Array<SimpleCommuneAvecStatutAffectation> = affectations
        .map((a) => {
          if (a.id === commune.id) {
            if (a.statut === "nouvelle-affectation") {
              return undefined;
            }
            return {
              ...a,
              statut: a.statut === "affectée" ? "affectation-supprimée" : "affectée",
            } satisfies SimpleCommuneAvecStatutAffectation;
          }
          return a;
        })
        .filter((a) => a != null);

      setAffectations(updatedAffectations.toSorted((a, b) => a.libelle.localeCompare(b.libelle)));
    },
    [affectations]
  );

  const onSave = useCallback((): void => {
    if (selectedAntenne == null) {
      return;
    }

    void catchErrorsOnSave(async () => {
      const payload: AffectationAntenneDto = {
        idAntenne: selectedAntenne.id,
        idsCommune: affectations
          .filter((a) => a.statut !== "affectation-supprimée")
          .map((a) => a.id),
      };
      const antenneUpdate = await antennesServices.affectationCommunes(selectedAntenne.id, payload);

      await networkService.waitForReplication(
        async () => {
          if (!(await isAffectationReplicated(antenneUpdate))) {
            throw new SdeappsError("Modification pas encore répliquée");
          }
        },
        20,
        () => {
          void mutateAntennes();
          onAntenneChange(antenneUpdate);
        },
        () => {
          enqueueSnackbar({ variant: "warning", message: ToastMessages.LONG_REPLICATION });
        }
      );

      await mutateAntennes();
      onAntenneChange(antenneUpdate);
    });
  }, [affectations, catchErrorsOnSave, mutateAntennes, onAntenneChange, selectedAntenne]);

  const pluriel = affectations.length > 0 ? "s" : "";
  const isSaveEnabled =
    selectedAntenne != null && affectations.some((a) => a.statut !== "affectée");

  return (
    <Grid container size={12} spacing={4}>
      <Grid size={12}>
        <Typography>
          Une commune ne peut être liée qu'à une seule antenne au maximum. Si elle est déjà associée
          à une autre antenne, la modification remplacera l'ancienne affectation par la nouvelle.
        </Typography>
      </Grid>
      <Grid container size={12} spacing={2}>
        <Grid size={6}>
          <Autocomplete
            disabled={isLoadingAntennes}
            fullWidth
            autoHighlight
            openOnFocus
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={antennes}
            filterOptions={filterOptionsAntenne}
            value={selectedAntenne ?? null}
            onChange={handleAntenneOnChange}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            renderInput={(params) => (
              <TextField {...params} label="Antenne *" placeholder="Antenne" />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Autocomplete
            disabled={isLoadingCommunes || selectedAntenne == null}
            fullWidth
            autoHighlight
            openOnFocus
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={communes}
            filterOptions={filterOptionsCommune}
            value={null}
            blurOnSelect
            onChange={handleCommuneOnSelect}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            renderInput={(params) => (
              <TextField {...params} label="Ajouter une commune" placeholder="Nom de la commune" />
            )}
          />
        </Grid>
      </Grid>
      {selectedAntenne != null && (
        <Grid container size={12}>
          <Grid size={12}>
            <AffectationAntenneCommunesLegende />
          </Grid>
          <Grid size={12}>
            <Typography>
              Commune{pluriel} affectée{pluriel} à l'antenne de <b>{selectedAntenne.libelle}</b>
            </Typography>
          </Grid>
          <Grid container size={12} spacing={1}>
            {affectations.map((commune) => (
              <Chip
                key={commune.id}
                label={commune.libelle}
                onDelete={handleOnDelete(commune)}
                color={getColorFrom(commune)}
              />
            ))}
          </Grid>
        </Grid>
      )}

      <Grid container size={12} sx={{ justifyContent: "flex-end" }} spacing={1}>
        <Button href={routesConfig.admin.path} color="error">
          Annuler
        </Button>
        <Button variant="contained" disabled={!isSaveEnabled} loading={isSaving} onClick={onSave}>
          Enregistrer
        </Button>
      </Grid>
    </Grid>
  );
}
